import {
  columnsNames,
  hvacColumnsNames,
  ambientColumnsNames,
} from "./constant";
import { Chip } from "@material-ui/core";
import { styled } from "@material-ui/styles";
import CustomSelectEditor from "./CustomSelects";
import "../remediationDocumentStyles.css";
import SelectDropDown from "main/components/SelectDropdown/SelectDropDown";
import CustomFilters from "main/components/CustomFilters/CustomSetFilter";

export const compareSampleReports = (
  oldReport,
  newReport,
  isDragged = false
) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update observation.name, tier, and result if changed
      if (existingItem.observation.name !== newItem.observation.name) {
        existingItem.observation.name = newItem.observation.name;
      }

      if (existingItem.room_name?.name !== newItem.room_name?.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.status !== newItem.status) {
        existingItem.status = newItem.status;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted rows
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort by observation name order in oldReport
  const sorted = Object.keys(grouped).reduce((res, room) => {
    const items = grouped[room];
    const sortedItems = items.sort((a, b) => {
      const indexA = oldReport.findIndex(
        (i) => i.observation.name === a.observation.name
      );
      const indexB = oldReport.findIndex(
        (i) => i.observation.name === b.observation.name
      );
      return (
        (indexA === -1 ? Infinity : indexA) -
        (indexB === -1 ? Infinity : indexB)
      );
    });
    res.push(...sortedItems);
    return res;
  }, []);

  return sorted;
};

export const compareHvacReports = (oldReport, newReport, isDragged = false) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update room name if changed
      if (existingItem.room_name.name !== newItem.room_name.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted items
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room name
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort room groups alphabetically and flatten
  const sorted = Object.keys(grouped)
    .sort() // alphabetical sort by room name
    .reduce((res, room) => {
      res.push(...grouped[room]);
      return res;
    }, []);

  return sorted;
};

export const comparAmbientReports = (
  oldReport,
  newReport,
  isDragged = false
) => {
  const reportMap = new Map(oldReport.map((item) => [item.id, item]));

  const roomPositions = new Map();
  oldReport.forEach((item, index) => {
    if (!roomPositions.has(item.room_name.name)) {
      roomPositions.set(item.room_name.name, index);
    }
  });

  newReport.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      // Update room name if changed
      if (existingItem.room_name.name !== newItem.room_name.name) {
        existingItem.room_name.name = newItem.room_name.name;
      }

      if (existingItem.tier !== newItem.tier) {
        existingItem.tier = newItem.tier;
      }

      if (existingItem.result !== newItem.result) {
        existingItem.result = newItem.result;
      }
    } else {
      const roomIndex = roomPositions.get(newItem.room_name.name);
      if (roomIndex !== undefined) {
        oldReport.splice(roomIndex + 1, 0, newItem);
      } else {
        oldReport.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted items
  const newIds = new Set(newReport.map((item) => item.id));
  Array.from(reportMap.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  });

  if (isDragged) {
    return Array.from(reportMap.values());
  }

  // Group by room name
  const grouped = Array.from(reportMap.values()).reduce((acc, item) => {
    const room = item.room_name.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort room groups alphabetically and flatten
  const sorted = Object.keys(grouped)
    .sort() // alphabetical sort by room name
    .reduce((res, room) => {
      res.push(...grouped[room]);
      return res;
    }, []);

  return sorted;
};

export const compareReportData = (
  oldData,
  newData,
  options = {
    fieldsToCompare: [],
    sortByField: null,
    isDragged: false,
    groupByRoom: true,
  }
) => {
  const { fieldsToCompare, sortByField, isDragged, groupByRoom } = options;

  const reportMap = new Map(oldData.map((item) => [item.id, item]));

  // Store original room positions
  const roomPositions = new Map();
  oldData.forEach((item, index) => {
    const roomName = item.room_name?.name;
    if (roomName && !roomPositions.has(roomName)) {
      roomPositions.set(roomName, index);
    }
  });

  newData.forEach((newItem) => {
    if (reportMap.has(newItem.id)) {
      const existingItem = reportMap.get(newItem.id);

      fieldsToCompare.forEach((field) => {
        const oldValue = field
          .split(".")
          .reduce((obj, key) => obj?.[key], existingItem);
        const newValue = field
          .split(".")
          .reduce((obj, key) => obj?.[key], newItem);

        if (oldValue !== newValue) {
          let ref = existingItem;
          const parts = field.split(".");
          parts.forEach((key, idx) => {
            if (idx === parts.length - 1) {
              ref[key] = newValue;
            } else {
              ref = ref[key];
            }
          });
        }
      });
    } else {
      const roomIndex = roomPositions.get(newItem.room_name?.name);
      if (roomIndex !== undefined) {
        oldData.splice(roomIndex + 1, 0, newItem);
      } else {
        oldData.push(newItem);
      }
      reportMap.set(newItem.id, newItem);
    }
  });

  // Remove deleted rows
  const newIds = new Set(newData.map((item) => item.id));
  for (const id of reportMap.keys()) {
    if (!newIds.has(id)) {
      reportMap.delete(id);
    }
  }

  const finalList = Array.from(reportMap.values());

  if (isDragged) return finalList;

  if (!groupByRoom) return finalList;

  // Group by room name
  const grouped = finalList.reduce((acc, item) => {
    const room = item.room_name?.name;
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  // Sort based on a field (if provided)
  const sorted = Object.keys(grouped).reduce((res, room) => {
    let items = grouped[room];
    if (sortByField) {
      items = items.sort((a, b) => {
        const aValue = sortByField
          .split(".")
          .reduce((obj, key) => obj?.[key], a);
        const bValue = sortByField
          .split(".")
          .reduce((obj, key) => obj?.[key], b);
        return aValue?.localeCompare?.(bValue) ?? 0;
      });
    }
    res.push(...items);
    return res;
  }, []);

  return sorted;
};

export const CustomTestDropDown = (
  options,
  onUpdate,
  props,
  rowIndex,
  data
) => {
  return (
    <SelectDropDown
      id={rowIndex}
      placeholder="Select .."
      options={options}
      handleOnChange={(e) => {
        const updatedTest = {
          id: e.value,
          name: e.label,
        };
        onUpdate(updatedTest);
      }}
      searchValue={options.find(
        (option) => Number(option.value) === Number(props.defaultValue.id)
      )}
      isSearchable={false}
      isClearable={false}
    />
  );
};

export const CustomReasonDropDown = (
  options,
  onUpdate,
  props,
  rowIndex,
  data
) => {
  return (
    <SelectDropDown
      placeholder="Select .."
      options={options}
      handleOnChange={(e) => {
        const updatedReason = {
          id: e.value,
          name: e.label,
        };
        onUpdate(updatedReason);
      }}
      searchValue={options.find(
        (option) => Number(option.value) === Number(props.defaultValue.id)
      )}
      isSearchable={false}
      isClearable={false}
    />
  );
};

export const getNestedValue = (obj, path) => {
  const keys = path.split(".");
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );
};

export const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  keys.reduce((acc, key, index) => {
    if (index === keys.length - 1) {
      acc[key] = value;
    }
    return acc[key] !== undefined ? acc[key] : (acc[key] = {});
  }, obj);
};

const handleRoomUpdateLogic = (row, data) => {
  let updatedRoom = "";
  updatedRoom = row?.room_name;

  data.forEach((li) => {
    if (li.room_name?.id === updatedRoom.id) {
      li.room_name = JSON.parse(JSON.stringify(updatedRoom));
    }
  });
};

const handleObservationUpdateLogic = (row, data) => {
  let updatedObservation = "";
  updatedObservation = row?.observation;
  data.forEach((li) => {
    if (li.observation_id === row.observation_id) {
      li.observation = JSON.parse(JSON.stringify(updatedObservation));
    }
  });
};

const handleTestUpdateLogic = (row, data, testList) => {
  const selectedTest = row?.test;
  const selectedTestSampleCost = testList.find(
    (test) => Number(test.id) === Number(selectedTest.id)
  );
  if (
    selectedTestSampleCost &&
    selectedTestSampleCost?.test_samples.length > 0
  ) {
    const cost = selectedTestSampleCost?.test_samples[0]?.cost;
    const sample_id = selectedTestSampleCost?.test_samples[0]?.id;
    data.forEach((li) => {
      if (li?.id === row.id) {
        li.test.sample_id = sample_id;
        li.test_sample.cost = cost;
      }
    });
  }
};

const handleRowSelection = (params, tableKey, handleSelectRow) => {
  const row = params.data;
  return (
    <div
      className={"checkInputCnt"}
      onClick={() => {
        handleSelectRow(row, tableKey);
      }}
    >
      <input
        className={"select_checkbox"}
        type="checkbox"
        checked={row[columnsNames.status.value]}
      />
    </div>
  );
};

const handleAllCheck = (tableKey, selectAllRows, handleAllSelectRow) => {
  return (
    <div className="checkInputCnt">
      <input
        className="select_checkbox"
        style={{ marginTop: "0px !important" }}
        type="checkbox"
        checked={selectAllRows?.[tableKey]}
        onChange={(e) => handleAllSelectRow(e, tableKey)}
      />
    </div>
  );
};

const ApprovedChip = styled(Chip)({
  color: "#5DC346",
  borderColor: "#5DC346",
  backgroundColor: "#E7F8E3",
  borderRadius: 4,
  fontSize: "10px",
  fontWeight: 500,
});

const UnapprovedChip = styled(Chip)({
  color: "#ED1B23",
  borderColor: "#ED1B23",
  backgroundColor: "#FFF0F0",
  borderRadius: 4,
  fontSize: "10px",
  fontWeight: 500,
});

const sharedColumns = {
  selection: ({
    tableKey,
    selectAllRows,
    handleSelectRow,
    handleAllSelectRow,
  }) => ({
    //headerComponent: (event) => <SnackBar event={event} />,
    headerName: "Sr",
    valueGetter: (params) => params.node.rowIndex + 1,
    editable: false,
    width: 40,
    filter: false,
    sortable: false,
    suppressMovable: true,
    onCellClicked: (event) => {
      const isSelected = event.node.isSelected();
      event.node.setSelected(!isSelected);
    },

    cellStyle: { textAlign: "center" },
  }),
  drag: {
    headerName: "",
    field: "drag",
    rowDrag: true,
    rowDragMultiRow: true,
    width: 50,
    sortable: false,
    filter: false,
    suppressMovable: true,
    suppressMoveWhenRowDragging: true,
  },
  roomName: ({ data, columnNames }) => ({
    headerName: columnNames?.room?.name,
    field: columnNames?.room?.value,
    editable: true,
    flex: 2,
    minWidth: 130,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    cellStyle: { textAlign: "left" },
    valueFormatter: (params) => params.value || "-",
    filter: "agTextColumnFilter",
    filterParams: { filterOptions: ["contains"], maxNumConditions: 1 },
    sortable: false,
    suppressMovable: true,
    valueSetter: (params) => {
      if (params.newValue !== params.data.room_name?.name) {
        params.data.room_name = {
          ...params.data.room_name,
          name: params.newValue,
          edit_col: true,
        };
        handleRoomUpdateLogic(params.data, data);
        return true;
      }
      return false;
    },
  }),
  sampleNote: ({ columnNames }) => ({
    headerName: columnNames.sample_note.name,
    field: columnNames.sample_note.value,
    editable: true,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    flex: 2,
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
  }),
  result: ({ columnNames }) => ({
    headerName: columnNames.result.name,
    field: columnNames.result.value,
    editable: true,
    flex: 2,
    cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
  }),
  status: ({ columnNames, handleStatusRow, tableKey }) => ({
    headerName: columnNames.status.name,
    field: columnNames.status.value,
    editable: false,
    flex: 1,
    minWidth: 100,
    filter: false,
    sortable: false,
    suppressMovable: true,
    cellClass: "center-cell",
    cellRenderer: (params) => {
      const isApproved = params.value === true;
      return isApproved ? (
        <ApprovedChip
          label="Approved"
          onClick={() => handleStatusRow(params.data, tableKey)}
          variant="outlined"
          size="small"
        />
      ) : (
        <UnapprovedChip
          label="Unapproved"
          onClick={() => handleStatusRow(params.data, tableKey)}
          variant="outlined"
          size="small"
        />
      );
    },
  }),
  tier: ({ tiers, tableKey, selectedTier, setSelectedTier, columnNames }) => ({
    headerComponent: () => (
      <CustomFilters
        header="Tier"
        tableKey={tableKey}
        testList={tiers.tier.map((test) => test.prefix)}
        selectedOptions={selectedTier}
        setSelectedOptions={setSelectedTier}
      />
    ),
    headerName: columnNames.tier.name,
    field: columnNames.tier.value,
    editable: true,
    flex: 1,
    minWidth: 100,
    sortable: false,
    suppressMovable: true,
    cellEditor: CustomSelectEditor,
    cellEditorParams: { values: tiers.tier, tableKey: "tier" },
    valueGetter: (params) => params.data.tier || "",
    valueSetter: (params) => {
      const selectedTier = tiers.tier.find(
        (tier) => tier.prefix === params.newValue.prefix
      );
      if (selectedTier) {
        params.data.tier = selectedTier.prefix;
        return true;
      }
      return false;
    },
    cellClassRules: {
      "tier-a-class": (params) =>
        params.value === "A" ||
        params.value === "Basic Cleaning" ||
        params.value === "Deep Cleaning" ||
        params.value === "Full Pack Out" ||
        params.value === "Clean" ||
        params.value === "Remove" ||
        params.value === "Pending",
      "tier-b-class": (params) => params.value === "B",
      "tier-c-class": (params) => params.value === "C",
      "tier-d-class": (params) => params.value === "D",
      "tier-e-class": (params) => params.value === "E",
      "tier-i-class": (params) => params.value === "I",
      "tier-p-class": (params) => params.value === "P",
    },
  }),

  delete: ({ handleDeleteRow, tableKey }) => ({
    headerName: "",
    field: "",
    width: 55,
    filter: false,
    sortable: false,
    suppressMovable: true,
    cellClass: "center-cell",
    cellRenderer: (params) => (
      <img
        src={require("assets/icons/delete.png").default}
        alt="icon"
        className="delete-icon"
        onClick={() =>
          handleDeleteRow({ row: params.data, tableKey: tableKey })
        }
        style={{ cursor: "pointer" }}
      />
    ),
  }),
};

const tableSpecificColumns = {
  remediation: ({
    data,
    tableKey,
    testListOptions,
    selectedOptions,
    setSelectedOptions,
    columnNames,
  }) => ({
    sampleType: {
      headerComponent: () => (
        <CustomFilters
          header="Sample Type"
          tableKey={tableKey}
          testList={testListOptions.map((test) => test.name)}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
        />
      ),
      headerName: columnNames.sample_type.name,
      field: columnNames.sample_type.value,
      editable: true,
      flex: 2,
      minWidth: 160,
      sortable: false,
      suppressMovable: true,
      cellEditor: CustomSelectEditor,
      cellEditorParams: {
        label: "- Select Type -",
        values: testListOptions,
        tableKey,
      },
      valueGetter: (params) => params.data.test?.name || "",
      valueSetter: (params) => {
        const selectedTest = testListOptions.find(
          (test) => test.id === params.newValue.id
        );
        if (selectedTest) {
          params.data.test = {
            id: selectedTest.id,
            name: selectedTest.name,
            edit_col: true,
          };
          handleTestUpdateLogic(params.data, data, testListOptions);
          return true;
        }
        return false;
      },
    },
    sampleLocation: {
      headerName: columnNames.sample_location.name,
      field: columnNames.sample_location.value,
      editable: true,
      flex: 1,
      minWidth: 130,
      cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
      filter: false,
      sortable: false,
      suppressMovable: true,
      valueGetter: (params) => params.data.sample_info?.[0]?.value || "",
      valueSetter: (params) => {
        const sampleInfo = params.data.sample_info?.[0];
        if (sampleInfo) {
          sampleInfo.value = params.newValue || "";
          return true;
        }
        return false;
      },
    },
    observation: {
      headerName: columnNames.observation.name,
      field: columnNames.observation.value,
      editable: true,
      flex: 1,
      minWidth: 150,
      cellEditorParams: { type: "text", rows: 4, maxLength: 50 },
      cellStyle: { textAlign: "left" },
      filter: "agTextColumnFilter",
      filterParams: { filterOptions: ["contains"], maxNumConditions: 1 },
      sortable: false,
      suppressMovable: true,
      valueSetter: (params) => {
        if (params.newValue !== params.data.observation?.name) {
          params.data.observation = {
            ...params.data.observation,
            name: params.newValue,
            edit_col: true,
          };
          handleObservationUpdateLogic(params.data, data);
          return true;
        }
        return false;
      },
    },
  }),
};

// Main column generator functions
export const getColumnsOfRemediationTable = (props) => {
  const {
    data,
    tiers,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    handleSelectRow,
    handleDeleteRow,
    setSelectedTier,
    testListOptions,
    selectedOptions,
    handleAllSelectRow,
    setSelectedOptions,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: columnsNames }),
    tableSpecificColumns.remediation({
      data,
      tableKey,
      testListOptions,
      selectedOptions,
      setSelectedOptions,
      columnNames: columnsNames,
    }).sampleType,
    tableSpecificColumns.remediation({ columnNames: columnsNames })
      .sampleLocation,
    sharedColumns.sampleNote({ columnNames: columnsNames }),
    tableSpecificColumns.remediation({ columnNames: columnsNames, data })
      .observation,
    sharedColumns.result({ columnNames: columnsNames }),
    sharedColumns.status({
      tableKey,
      columnNames: columnsNames,
      handleStatusRow,
    }),
    sharedColumns.tier({
      tiers,
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: columnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey }),
  ];
};

export const getColumnsOfHvacTable = (props) => {
  const {
    data,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    setSelectedTier,
    handleSelectRow,
    handleDeleteRow,
    handleAllSelectRow,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: hvacColumnsNames }),
    sharedColumns.sampleNote({ columnNames: hvacColumnsNames }),
    sharedColumns.result({ columnNames: hvacColumnsNames }),
    sharedColumns.status({
      tableKey,
      columnNames: hvacColumnsNames,
      handleStatusRow,
    }),
    sharedColumns.tier({
      tiers: {
        tier: [
          { id: 1, prefix: "Clean", title: "Clean" },
          { id: 2, prefix: "Remove", title: "Remove" },
          { id: 4, prefix: "P", title: "P" },
        ],
      },
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: hvacColumnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey }),
  ];
};

export const getColumnsOfAmbientTable = (props) => {
  const {
    data,
    tableKey,
    selectedTier,
    selectAllRows,
    handleStatusRow,
    setSelectedTier,
    handleSelectRow,
    handleDeleteRow,
    handleAllSelectRow,
  } = props;

  return [
    sharedColumns.selection({
      tableKey,
      selectAllRows,
      handleSelectRow,
      handleAllSelectRow,
    }),
    sharedColumns.drag,
    sharedColumns.roomName({ data, columnNames: ambientColumnsNames }),
    sharedColumns.sampleNote({ columnNames: ambientColumnsNames }),
    sharedColumns.result({ columnNames: ambientColumnsNames }),
    sharedColumns.status({
      columnNames: ambientColumnsNames,
      handleStatusRow,
      tableKey,
    }),
    sharedColumns.tier({
      tiers: {
        tier: [
          { id: 1, prefix: "Basic Cleaning", title: "Basic Cleaning" },
          { id: 2, prefix: "Deep Cleaning", title: "Deep Cleaning" },
          { id: 3, prefix: "Full Pack Out", title: "Full Pack Out" },
          { id: 4, prefix: "P", title: "P" },
        ],
      },
      tableKey,
      selectedTier,
      setSelectedTier,
      columnNames: ambientColumnsNames,
    }),
    sharedColumns.delete({ handleDeleteRow, tableKey }),
  ];
};
